import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useCollaborationEmail } from '@/contexts/CollaborationEmailContext';
import { useCollaborationEmails } from '@/hooks/useCollaborationEmails';
import { useCollaborationTypes } from '@/hooks/useCollaborationTypes';
import { useBrandManager } from '@/contexts/BrandProvider';
import { toast } from 'sonner';
import { Code } from 'lucide-react';
import { ShortcodePopup } from '@/components/visual-generation/ShortcodePopup';
import { MultiSelect } from '@/components/ui/multi-select';
import type { CollaborationCampaignTriggerConditions } from '@/types/collaborations';
import { COLLABORATOR_STATUS_OPTIONS } from '@/types/collaborations';

const defaultStopConditions: CollaborationCampaignTriggerConditions = {
  collaborator_status: [],
  collaboration_type_ids: [],
};

function buildStopConditions(tc: CollaborationCampaignTriggerConditions | null | undefined): CollaborationCampaignTriggerConditions | null {
  if (!tc) return null;
  const collaborator_status = (tc.collaborator_status ?? []).filter(Boolean);
  const collaboration_type_ids = (tc.collaboration_type_ids ?? []).filter(Boolean);
  if (collaborator_status.length === 0 && collaboration_type_ids.length === 0) return null;
  const out: CollaborationCampaignTriggerConditions = {};
  if (collaborator_status.length > 0) out.collaborator_status = collaborator_status;
  if (collaboration_type_ids.length > 0) out.collaboration_type_ids = collaboration_type_ids;
  return out;
}

export const GlobalEmailDialog = () => {
  const { isOpen, editingEmail, campaignId, closeEmailDialog } = useCollaborationEmail();
  const { currentBrand } = useBrandManager();
  const { createEmail, updateEmail } = useCollaborationEmails(campaignId);
  const { types: collaborationTypes } = useCollaborationTypes(currentBrand?.id ?? null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [showShortcodePopup, setShowShortcodePopup] = useState(false);
  const [insertTarget, setInsertTarget] = useState<'subject' | 'content'>('content');

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      subject: '',
      content: '',
      sequence_order: 1,
      delay_days: 0,
      delay_hours: 0,
      send_conditions: {
        stop_if_replied: true,
        stop_if_opened: false
      },
      stop_conditions: defaultStopConditions
    }
  });

  // Reset form when dialog opens or editing email changes
  useEffect(() => {
    if (isOpen && editingEmail) {
      reset({
        subject: editingEmail.subject,
        content: editingEmail.content,
        sequence_order: editingEmail.sequence_order,
        delay_days: editingEmail.delay_days,
        delay_hours: editingEmail.delay_hours,
        send_conditions: editingEmail.send_conditions ?? { stop_if_replied: true, stop_if_opened: false },
        stop_conditions: (editingEmail as any).stop_conditions
          ? {
              collaborator_status: (editingEmail as any).stop_conditions.collaborator_status ?? [],
              collaboration_type_ids: (editingEmail as any).stop_conditions.collaboration_type_ids ?? []
            }
          : defaultStopConditions
      });
    } else if (isOpen && !editingEmail) {
      reset({
        subject: '',
        content: '',
        sequence_order: 1,
        delay_days: 0,
        delay_hours: 0,
        send_conditions: {
          stop_if_replied: true,
          stop_if_opened: false
        },
        stop_conditions: defaultStopConditions
      });
    }
  }, [isOpen, editingEmail, reset]);

  const stopIfReplied = watch('send_conditions.stop_if_replied');
  const stopIfOpened = watch('send_conditions.stop_if_opened');
  const subjectValue = watch('subject') || '';
  const contentValue = watch('content') || '';

  const openShortcodePopup = (target: 'subject' | 'content') => {
    setInsertTarget(target);
    setShowShortcodePopup(true);
  };

  const handleInsertShortcode = (shortcode: string) => {
    if (insertTarget === 'subject') {
      const input = subjectRef.current;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = subjectValue.slice(0, start) + shortcode + subjectValue.slice(end);
        setValue('subject', newValue);
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + shortcode.length, start + shortcode.length);
        }, 0);
      } else {
        setValue('subject', subjectValue + shortcode);
      }
    } else {
      const textarea = contentRef.current;
      if (textarea) {
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const newValue = contentValue.slice(0, start) + shortcode + contentValue.slice(end);
        setValue('content', newValue);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + shortcode.length, start + shortcode.length);
        }, 0);
      } else {
        setValue('content', contentValue + shortcode);
      }
    }
    setShowShortcodePopup(false);
  };

  const onSubmit = async (data: any) => {
    if (!campaignId) {
      toast.error('Campaign ID is missing');
      return;
    }

    try {
      const stop_conditions = buildStopConditions(data.stop_conditions ?? null);
      const payload = { ...data, stop_conditions: stop_conditions ?? undefined };
      if (editingEmail) {
        await updateEmail({ id: editingEmail.id, ...payload });
        toast.success('Email updated successfully');
      } else {
        await createEmail(payload);
        toast.success('Email created successfully');
      }
      closeEmailDialog();
    } catch (error) {
      console.error('Error saving email:', error);
      toast.error('Failed to save email');
    }
  };

  if (!campaignId) return null;

  return (
    <Dialog open={isOpen} onOpenChange={closeEmailDialog}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={editingEmail ? "Edit Campaign Email" : "Create Campaign Email"}
            popupContext={`/collaboration-campaigns/${campaignId}/emails`}
            isOpen={isOpen}
          >
            {editingEmail ? 'Edit' : 'Add'} Email
          </DialogTitleWithBug>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="sequence_order">Sequence Order *</Label>
              <Input 
                id="sequence_order" 
                type="number" 
                min="1"
                {...register('sequence_order', { required: true, valueAsNumber: true })} 
              />
            </div>
            <div>
              <Label htmlFor="delay_days">Delay (Days)</Label>
              <Input 
                id="delay_days" 
                type="number" 
                min="0"
                {...register('delay_days', { valueAsNumber: true })} 
              />
            </div>
            <div>
              <Label htmlFor="delay_hours">Delay (Hours)</Label>
              <Input 
                id="delay_hours" 
                type="number" 
                min="0"
                max="23"
                {...register('delay_hours', { valueAsNumber: true })} 
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="subject">Email Subject *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => openShortcodePopup('subject')}
                className="h-8 w-8 p-0 hover:bg-muted"
                title="Available Shortcodes"
              >
                <Code className="h-4 w-4" />
              </Button>
            </div>
            <Input 
              id="subject" 
              value={subjectValue}
              onChange={(e) => setValue('subject', e.target.value)}
              placeholder="Subject line for this email"
              ref={subjectRef}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="content">Email Content *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => openShortcodePopup('content')}
                className="h-8 w-8 p-0 hover:bg-muted"
                title="Available Shortcodes"
              >
                <Code className="h-4 w-4" />
              </Button>
            </div>
            <Textarea 
              id="content" 
              value={contentValue}
              onChange={(e) => setValue('content', e.target.value)}
              placeholder="Email body content..."
              rows={10}
              ref={contentRef}
            />
          </div>

          <div className="space-y-3 border-t pt-4">
            <Label>Send Conditions</Label>
            <div className="flex items-center justify-between">
              <Label htmlFor="stop_if_replied" className="font-normal">
                Stop sequence if recipient replied
              </Label>
              <Switch
                id="stop_if_replied"
                checked={stopIfReplied}
                onCheckedChange={(checked) => setValue('send_conditions.stop_if_replied', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="stop_if_opened" className="font-normal">
                Stop sequence if recipient opened previous email
              </Label>
              <Switch
                id="stop_if_opened"
                checked={stopIfOpened}
                onCheckedChange={(checked) => setValue('send_conditions.stop_if_opened', checked)}
              />
            </div>
          </div>

          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium">Stop when (optional)</h4>
            <p className="text-xs text-muted-foreground">
              Stop sending this step when the collaborator matches any of these conditions.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Collaborator status</Label>
                <MultiSelect
                  options={COLLABORATOR_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
                  selected={watch('stop_conditions.collaborator_status') ?? []}
                  onChange={(v) => setValue('stop_conditions.collaborator_status', v)}
                  placeholder="Any"
                />
              </div>
              <div>
                <Label className="text-xs">Collaboration type</Label>
                <MultiSelect
                  options={collaborationTypes.map((t) => ({ label: t.name, value: t.id }))}
                  selected={watch('stop_conditions.collaboration_type_ids') ?? []}
                  onChange={(v) => setValue('stop_conditions.collaboration_type_ids', v)}
                  placeholder="Any"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeEmailDialog}>Cancel</Button>
            <Button type="submit">{editingEmail ? 'Update' : 'Create'}</Button>
          </div>
        </form>

        <ShortcodePopup
          open={showShortcodePopup}
          onOpenChange={setShowShortcodePopup}
          onInsert={handleInsertShortcode}
        />
      </DialogContent>
    </Dialog>
  );
};
